<script setup lang="ts">
import { useAppStore } from '~/stores/app'
const { t } = useAppI18n()
type LicenseChangeAttachment = { path?: string; name?: string }
type LicenseChangeInfo = { info?: string; prefixIcon?: string }
type LicenseChangeItem = {
  logDate?: string
  changeContent?: string
  intervalTime?: string
  changeData?: { beforeData?: LicenseChangeInfo[]; afterData?: LicenseChangeInfo[] }
  attachments?: LicenseChangeAttachment[]
}

const props = withDefaults(
  defineProps<{ show?: boolean; regulationId?: string; projectCode?: string; result?: LicenseChangeItem[] }>(),
  { show: false, regulationId: '', projectCode: '', result: () => [] },
)
const emit = defineEmits<{ closeReghisFn: [] }>()
const appStore = useAppStore()
const route = useRoute()
const showReghis = ref(false)
const LanguageCode = computed(() => appStore.LanguageCode)
const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())
const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) return normalizeText(value[0])
  return normalizeText(value)
}
const exchangeSlug = computed(() => getFirstRouteValue(route.params.slug))
const buildCertificateHref = (index: number) => {
  const query = new URLSearchParams()

  if (props.regulationId) query.set('regulationId', props.regulationId)
  query.set('idx', String(index))

  if (exchangeSlug.value) return `/${LanguageCode.value}/exchanges/${exchangeSlug.value}/licenses?${query.toString()}`

  return ''
}

watch(
  () => props.show,
  (v) => {
    showReghis.value = v
    setHeight()
  },
)

const closeAll = () => emit('closeReghisFn')
const setHeight = () => {
  nextTick(() => {
    if (!import.meta.client) return
    const licensePopup = document.querySelector('.license-popup') as HTMLElement | null
    const reghisPopup = document.querySelector('.reghis-popup') as HTMLElement | null
    if (licensePopup && reghisPopup) reghisPopup.style.height = `${licensePopup.offsetHeight}px`
  })
}
</script>

<template>
  <ElDialog
    v-model="showReghis"
    custom-class="reghis-popup"
    append-to-body
    width="800px"
    class="reghis-dialog-regulatory"
    :close-on-click-modal="false"
    @close="closeAll"
  >
    <template #header>
      <div class="license-history-dialog-header">
        <h2 class="license-history-dialog-title">{{ t('84571') }}</h2>
        <button type="button" class="license-history-dialog-close" @click.stop="closeAll">
          <CommonImage
            src="https://resources1.interface003.com/web20/img/dealer/licence/licence-close.svg"
            alt=""
            class="license-history-dialog-close-icon"
            object-fit="contain"
          />
        </button>
      </div>
    </template>
    <div>
      <div
        v-for="(items, idxx) in result"
        :key="`${items.changeContent || ''}-${idxx}`"
        class="flex space-x-[14px] rtl:space-x-reverse first:[&_.tttw]:border-none"
      >
        <div class="flex w-[10px] flex-col items-center">
          <span class="tttw h-[4px] border-l-[1px] border-dashed border-[#C9CDD4]"></span>
          <span
            class="h-[10px] w-[10px] rounded-[5px] border-[2px] border-solid border-[#477DB2]"
            :class="idxx == 0 || idxx == result?.length - 1 ? 'border-[#477DB2]' : 'border-[#C9CDD4]'"
          ></span>
          <span
            class="flex-1 border-l-[1px] border-dashed border-[#C9CDD4]"
            :class="idxx === result.length - 1 ? 'hidden border-none' : ''"
          ></span>
        </div>
        <div class="flex-1 pb-[20px]">
          <div class="mb-[2px] flex space-x-[8px] rtl:space-x-reverse">
            <span class="text-[14px] leading-[20px] text-[#3D3D3D]/60">{{ items.logDate }}</span>
            <span class="text-[14px] leading-[20px] font-semibold text-[#3D3D3D]">{{ items.changeContent }}</span>
          </div>
          <p class="mb-[10px] flex text-[12px] leading-[17px] text-[#3D3D3D]/60">{{ items.intervalTime }}</p>
          <div
            v-if="items?.changeData?.beforeData?.[0]?.info || items?.changeData?.afterData?.[0]?.info"
            class="mb-[10px] flex bg-[#F7F8FA] px-[16px] py-[12px] sm:p-[12px]"
          >
            <div class="mr-[12px] flex-1 rtl:mr-[0px] rtl:ml-[12px]">
              <div class="mb-[6px] text-left text-[14px] text-[#86909C] rtl:text-right">
                {{ t('84573') }}
              </div>
              <p
                v-for="(tt, ind) in items?.changeData?.beforeData"
                :key="ind"
                class="mb-[4px] flex space-x-[6px] sm:m-[0px] rtl:space-x-reverse"
              >
                <CommonImage v-if="tt.prefixIcon" :src="tt.prefixIcon" :alt="tt.info" class="h-[16px] w-[16px]" />
                <span class="text-[14px] leading-[20px] text-[#3D3D3D]">{{ tt.info }}</span>
              </p>
            </div>
            <div class="flex-1">
              <div class="mb-[6px] text-left text-[14px] text-[#86909C] rtl:text-right">
                {{ t('84574') }}
              </div>
              <p
                v-for="(tt, idx) in items?.changeData?.afterData"
                :key="idx + 'a'"
                class="mb-[4px] flex space-x-[6px] sm:m-[0px] rtl:space-x-reverse"
              >
                <CommonImage v-if="tt.prefixIcon" :src="tt.prefixIcon" :alt="tt.info" class="h-[16px] w-[16px]" />
                <span class="text-[14px] leading-[20px] text-[#3D3D3D]">{{ tt.info }}</span>
              </p>
            </div>
          </div>
          <div v-if="items.attachments?.length" class="flex flex-wrap">
            <a
              :href="buildCertificateHref(idxx)"
              target="_blank"
              class="flex cursor-pointer items-center justify-center space-x-[4px] rounded-[2px] border-[0.5px] border-solid px-[8px] py-[4px] text-[#477DB2] rtl:space-x-reverse"
              style="background: rgba(71, 125, 178, 0.05); border-color: rgba(71, 125, 178, 0.3)"
            >
              <i class="iconfont icon-shijian text-[14px]"></i>
              <span class="text-[12px]">{{ t('84514') }} ({{ items.attachments.length }})</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </ElDialog>
</template>

<style scoped lang="less">
.license-history-dialog-header {
  display: flex;
  margin-bottom: 20px;
  padding: 0 0 16px 0;
  align-items: center;
  border-bottom: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
}

.license-history-dialog-title {
  margin: 0;
  color: #292a2e;
  font-family: var(--Font-Family--ZH-CN, Outfit);
  font-size: var(--Font-Size--XL-20, 20px);
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}

.license-history-dialog-close {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  margin-inline-start: auto;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.license-history-dialog-close-icon {
  display: block;
  width: 32px;
  height: 32px;
}

:global(.reghis-dialog-regulatory .el-dialog) {
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 16px;
  transform: translate(-50%, -50%);
}
:global(.reghis-dialog-regulatory .el-dialog__headerbtn) {
  display: none;
}
:global(.reghis-dialog-regulatory .el-dialog__header) {
  padding: 0;
}
:global(.reghis-dialog-regulatory .el-dialog__body) {
  text-align: left !important;
  max-height: 100% !important;
  overflow-y: auto !important;
  position: relative;
  margin: 16px 4px 32px;
  padding: 0 56px;
}
:global(.reghis-dialog-regulatory .el-dialog__body::-webkit-scrollbar) {
  background-color: transparent;
  width: 4px;
  margin-right: 8px;
}
:global(.reghis-dialog-regulatory .el-dialog__body::-webkit-scrollbar-thumb) {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
}
:global(.reghis-popup .el-dialog) {
  padding: 32px 60px;
}
</style>
